import { CotacaoEcommerce } from "@/types/Cotacao";
import { computeQuoteTotals, PaymentKind } from "@/utils/pricing";
import { clearError, dlPushEvent, resetDlState } from "./gtm";

export type StepName =
  | "step1"
  | "step2"
  | "final"
  | "inicio"
  | "cotacao"
  | "veiculo"
  | "pagamento"
  | "status";

const sendEcom = (
  eventName: string,
  ecommerce: Record<string, unknown>,
  extra?: Record<string, unknown>,
) => {
  resetDlState();
  dlPushEvent(eventName, { ecommerce, ...(extra || {}) });
};

const sendError = (
  eventName: string,
  errorObj: Record<string, unknown>,
  extra?: Record<string, unknown>,
) => {
  clearError();
  dlPushEvent(eventName, { error: errorObj, ...(extra || {}) });
};

export interface CotacaoStep1SubmitParams {
  origin_location_input: string;
  origin_location_code?: string;
  destination_location_input: string;
  destination_location_code?: string;
  vehicle_brand_code: string;
  vehicle_brand_label?: string;
  vehicle_value_brl: number;
  is_working: boolean;
  is_armored: boolean;
}

type PersonalOkPayload = {
  name: string;
  ddi: string;
  phone: string;
  email: string;
  reason?: string;
  transportSchedule: string;
};

const buildStepTwoEcommerce = (p: PersonalOkPayload) => ({
  usuario: {
    nome: p.name,
    ddi: p.ddi,
    telefone: p.phone,
    email: p.email,
  },
  motivo_codigo: p.reason || "",
  previsao_transporte: p.transportSchedule,
});

export function mapCotacaoToEcommerce(
  c: CotacaoEcommerce,
  payment?: { method?: PaymentKind; [k: string]: unknown },
) {
  const totals = computeQuoteTotals(c, payment?.method);

  return {
    currency: "BRL",
    value: totals.total,
    coupon: totals.coupon,
    coupon_code: totals.coupon_code,
    discount_value: totals.discount,
    discount_breakdown: totals.discount_breakdown,

    items: [
      {
        item_id: c.cve_codigo,
        item_name: c.vmd_descricao,
        item_variant: c.cor_descricao || undefined,
        item_brand: "Nacional GO",
        item_category: "Transporte de Veículos",

        origin_city: c.mun_descricao_origem,
        origin_base: c.bop_origem_descricao,
        destination_city: c.mun_descricao_destino,
        destination_base: c.bop_destino_descricao,
        route_text: c.cve_rota_texto,
        route_km: Number.isFinite(Number(c.cve_rota_kms))
          ? Number(c.cve_rota_kms)
          : undefined,

        price: totals.total,
        quantity: 1,

        insurance_value: Number.isFinite(Number(c.cve_valor_seguro))
          ? Number(c.cve_valor_seguro)
          : undefined,
        pickup_value: Number.isFinite(Number(c.cve_valor_coleta))
          ? Number(c.cve_valor_coleta)
          : undefined,
        delivery_value: Number.isFinite(Number(c.cve_valor_entrega))
          ? Number(c.cve_valor_entrega)
          : undefined,
        surcharge: Number.isFinite(Number(c.cve_valor_acrescimo))
          ? Number(c.cve_valor_acrescimo)
          : undefined,
        surcharge_vehicle: Number.isFinite(
          Number(c.cve_valor_acrescimo_veiculo),
        )
          ? Number(c.cve_valor_acrescimo_veiculo)
          : undefined,
        upsell_value: Number.isFinite(Number(c.cve_valor_upsell))
          ? Number(c.cve_valor_upsell)
          : undefined,

        armored: c.cve_blindado,
        working: c.cve_operante,
      },
    ],

    user: {
      fullname: c.ctc_contato || c.cve_coleta_nome || "",
      phone:
        Number(
          (c.ctc_telefone_1 || c.cve_coleta_tel1 || "").replace(/\D/g, ""),
        ) || undefined,
      email: c.ctc_email || c.cve_coleta_email || undefined,
    },

    quote: {
      id: c.cve_codigo,
      reason_code: c.cmc_codigo,
      reason_desc: c.cmc_descricao,
      prazo_entrega: c.cve_prazo_entrega,
      previsao_recebimento: c.cve_data_previsao_recebimento,
      tipo_seguro: c.cve_tipo_seguro,
      coleta_flag: c.tro_codigo === "2",
      entrega_flag: c.trd_codigo === "2",
      express_flag: c.cve_express_codigo === "1",
    },

    ...(payment ? { payment } : {}),
  };
}

export function trackCotacaoStepView(step: StepName) {
  sendEcom("cotacao_step_view", { step_name: step });
}

export function trackCotacaoStep1Submit(params: CotacaoStep1SubmitParams) {
  sendEcom("cotacao_localizacao_veiculo", {
    local_coleta: params.origin_location_input,
    local_coleta_code: params.origin_location_code,
    local_destino: params.destination_location_input,
    local_destino_code: params.destination_location_code,
    veiculo_marca_codigo: params.vehicle_brand_code,
    veiculo_marca_nome: params.vehicle_brand_label,
    valor_brl: params.vehicle_value_brl,
    moeda: "BRL",
    veiculo_funcionando: params.is_working,
    veiculo_blindado: params.is_armored,
  });
}

export function trackCotacaoValidacaoCepOk(
  field: "origin" | "destination",
  cepOrInput: string,
  mun_code?: string,
) {
  sendEcom("cotacao_validacao_cep_ok", {
    field,
    cep: cepOrInput,
    mun_code,
  });
}

export function trackCotacaoValidacaoCepErro(
  field: "origin" | "destination",
  input: string,
  reason: string,
) {
  sendError("cotacao_validacao_cep_erro", {
    field,
    input,
    reason,
  });
}

export function trackCotacaoDadosPessoaisSubmitSuccess(
  payload: PersonalOkPayload & { quote_id: string },
) {
  clearError();
  dlPushEvent("cotacao_dados_pessoais", {
    ecommerce: {
      ...buildStepTwoEcommerce(payload),
      quote_id: payload.quote_id,
    },
  });
}

export function trackCotacaoDadosPessoaisSubmitError(
  payload: PersonalOkPayload,
  err: { message?: string; code?: string } = {},
) {
  clearError();
  dlPushEvent("cotacao_dados_pessoais_submit_erro", {
    ecommerce: buildStepTwoEcommerce(payload),
    error: {
      type: "submit_error",
      message: err.message || "",
      code: err.code || "",
    },
  });
}

export function trackCotacaoDadosPessoaisCaptchaErro(
  payload: PersonalOkPayload,
) {
  clearError();
  dlPushEvent("cotacao_dados_pessoais_captcha_erro", {
    ecommerce: buildStepTwoEcommerce(payload),
    error: {
      type: "captcha",
      reason: "missing_or_expired",
    },
  });
}

export function trackCotacaoVoltarParaVeiculo() {
  resetDlState();
  dlPushEvent("cotacao_voltar_para_veiculo", {
    ecommerce: { from_step: "step2", to_step: "step1" },
  });
}

export function trackCotacaoApresentacao(c: CotacaoEcommerce) {
  resetDlState();
  dlPushEvent("cotacao_apresentacao", {
    ecommerce: mapCotacaoToEcommerce(c),
  });
}

export function trackInfoAdicional(c: CotacaoEcommerce) {
  resetDlState();
  dlPushEvent("cotacao_informacoes_adicionais", {
    ecommerce: mapCotacaoToEcommerce(c),
  });
}

export function trackCotacaoApresentacaoUpdate(
  c: CotacaoEcommerce,
  cause:
    | "price_changed"
    | "coupon_applied"
    | "coleta_toggle"
    | "entrega_toggle"
    | "seguro_toggle"
    | "operante_toggle"
    | "blindado_toggle"
    | "express_toggle"
    | "date_changed"
    | "cep_coleta_changed"
    | "cep_entrega_changed"
    | "vehicle_updated"
    | "recalculate"
    | "custom",
  details?: Record<string, unknown>,
) {
  resetDlState();
  dlPushEvent("cotacao_update", {
    ecommerce: mapCotacaoToEcommerce(c),
    update_cause: cause,
    ...(details ? { update_details: details } : {}),
  });
}

export function trackBeginCheckout(c: CotacaoEcommerce) {
  resetDlState();
  dlPushEvent("begin_checkout", {
    ecommerce: mapCotacaoToEcommerce(c),
  });
}

export function trackCotacaoCupomAplicado(
  c: CotacaoEcommerce,
  success: boolean,
  cupom?: {
    codigo?: string;
    nome?: string;
    valor?: number;
    tipo?: "PERCENTUAL" | "FIXO";
  },
) {
  resetDlState();
  dlPushEvent("cotacao_cupom_aplicado", {
    ecommerce: mapCotacaoToEcommerce(c),
    coupon_result: success ? "success" : "fail",
    coupon_info: cupom,
  });
}

export function trackCotacaoPurchase(
  c: CotacaoEcommerce,
  opts: {
    transaction_id: string;
    method: PaymentKind;
    installments?: number;
    brand?: string;
    last4?: string;
    boleto_url?: string;
    boleto_due_date?: string;
    pix_expires_at?: string;
    pix_qr_code?: string;
  },
) {
  resetDlState();

  const ecommerce = mapCotacaoToEcommerce(c, {
    method: opts.method,
    installments: opts.installments,
    brand: opts.brand,
    last4: opts.last4,
    boleto_url: opts.boleto_url,
    boleto_due_date: opts.boleto_due_date,
    pix_expires_at: opts.pix_expires_at,
  });

  dlPushEvent("purchase", {
    ecommerce: {
      ...ecommerce,
      transaction_id: opts.transaction_id,
    },
  });
}

export function trackCotacaoPurchaseError(
  c: CotacaoEcommerce,
  method: PaymentKind,
  error: { message?: string; code?: string },
) {
  resetDlState();
  dlPushEvent("cotacao_purchase_error", {
    ecommerce: mapCotacaoToEcommerce(c, { method }),
    error,
  });
}

export function trackCotacaoPaymentStatus(
  c: CotacaoEcommerce,
  transaction_id: string,
  status: "approved" | "pending" | "rejected",
) {
  resetDlState();
  dlPushEvent("cotacao_payment_status", {
    ecommerce: mapCotacaoToEcommerce(c),
    payment_status: status,
    transaction_id,
  });
}
